import React from 'react';
import { Briefcase, FolderOpen } from 'lucide-react';
import type { CustomerRecord } from '../CustomerLogin';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { PortalSnapshotStamps } from './PortalSnapshotStamps';
import { usePortalAppearance } from './portalAppearanceContext';
import { formatDocumentCount, resolveArbeitskontextLabel } from './portalArbeitskontext';

export interface PortalArbeitskontextBarProps {
  customer: Pick<CustomerRecord, 'kanzlei' | 'tier'>;
  documentCount?: number | null;
  placement?: 'sidebar-footer' | 'content';
  className?: string;
}

/** Kompakter Arbeitskontext: Kanzlei, Tarif, Rechtsstand, Dokumentenzähler. */
export const PortalArbeitskontextBar: React.FC<PortalArbeitskontextBarProps> = ({
  customer,
  documentCount,
  placement = 'content',
  className = '',
}) => {
  const arbeitskontext = resolveArbeitskontextLabel(customer);
  const docsLabel = formatDocumentCount(documentCount);
  const isSidebar = placement === 'sidebar-footer';
  const light = usePortalAppearance() === 'light' && isSidebar;
  const kanzleiTone = light ? '!bg-[#f3efe8] !text-[#1c3f73] !border-[#1c3f73]' : '';
  const kontextTone = light ? '!bg-[#f3efe8] !text-[#5b4678] !border-[#5b4678]' : '';

  const pillClass = isSidebar
    ? 'portal-sidebar-pill !px-2 !py-0.5 !text-[11px] !leading-tight !whitespace-normal text-left max-w-full'
    : '!px-2 !py-0.5 !text-[10px] truncate';

  const kanzleiBadge = (
    <PortalBadge
      label={customer.kanzlei}
      accent="blue"
      icon={<Briefcase className="w-2.5 h-2.5 flex-shrink-0" aria-hidden />}
      className={`${pillClass} ${kanzleiTone} ${isSidebar ? '' : 'max-w-[200px]'}`}
    />
  );

  const arbeitskontextBadge = arbeitskontext ? (
    <PortalBadge
      label={`Arbeitskontext: ${arbeitskontext}`}
      accent="violet"
      className={`${pillClass} ${kontextTone} ${isSidebar ? '' : 'max-w-[220px]'}`}
    />
  ) : null;

  const docsLine = (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 font-medium tracking-wide min-w-0">
      <FolderOpen className="w-3 h-3 flex-shrink-0" aria-hidden />
      <span className="truncate">Kanzlei-Arbeitsplatz</span>
      <span className="text-slate-600 flex-shrink-0" aria-hidden>
        ·
      </span>
      <span className="truncate">{docsLabel}</span>
    </span>
  );

  if (isSidebar) {
    return (
      <div className={`flex flex-col items-start gap-2 min-w-0 w-full ${className}`} aria-label="Arbeitskontext">
        {kanzleiBadge}
        {arbeitskontextBadge}
        <PortalSnapshotStamps
          size="xs"
          interactive
          className="!flex !w-full !min-w-0 !flex-col !flex-nowrap !items-start !gap-2"
        />
        <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0 w-full text-[11px] font-medium leading-snug ${light ? 'text-[#3f3832]' : 'text-slate-500'}`}>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap max-w-full">
            <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
            <span>Kanzlei-Arbeitsplatz</span>
          </span>
          <span className="whitespace-nowrap">{docsLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 px-1 py-2 mb-3 border-b border-white/[0.06] ${className}`}
      aria-label="Arbeitskontext"
    >
      {kanzleiBadge}
      {arbeitskontextBadge}
      <PortalSnapshotStamps size="xs" interactive />
      {docsLine}
    </div>
  );
};
